import type { RowSelectionState } from "@tanstack/react-table";

/**
 * Apply single-row or Shift+range selection for the visible (current page) row ids.
 * Mirrors typical list UX: first click anchors; Shift+click selects every row in between.
 */
export function applyDataTableRowCheckboxSelection(args: {
  previous: RowSelectionState;
  visibleRowIds: readonly string[];
  anchorRowId: string | null;
  targetRowId: string;
  selected: boolean;
  shiftKey: boolean;
}): { next: RowSelectionState; nextAnchorRowId: string } {
  const {
    previous,
    visibleRowIds,
    anchorRowId,
    targetRowId,
    selected,
    shiftKey,
  } = args;

  const next: RowSelectionState = { ...previous };

  if (shiftKey && anchorRowId) {
    const anchorIndex = visibleRowIds.indexOf(anchorRowId);
    const targetIndex = visibleRowIds.indexOf(targetRowId);
    if (anchorIndex >= 0 && targetIndex >= 0) {
      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      for (let i = start; i <= end; i++) {
        const id = visibleRowIds[i];
        if (selected) next[id] = true;
        else delete next[id];
      }
      return { next, nextAnchorRowId: targetRowId };
    }
  }

  if (selected) next[targetRowId] = true;
  else delete next[targetRowId];
  return { next, nextAnchorRowId: targetRowId };
}

/** Selection-shift helpers mounted on `table.options.meta` by `DataTable`. */
export type DataTableSelectionMeta = {
  getAnchor: () => string | null;
  setAnchor: (rowId: string | null) => void;
  markShiftHandled: () => void;
  /** Returns true when the next onCheckedChange should be ignored. */
  consumeShiftHandled: () => boolean;
  /** Clear shift-handled sticker when a non-shift press starts. */
  notePointerShift: (shiftKey: boolean) => void;
};

export type DataTableMeta = {
  selection?: DataTableSelectionMeta;
};

export function getDataTableSelectionMeta(
  meta: unknown
): DataTableSelectionMeta | undefined {
  if (!meta || typeof meta !== "object") return undefined;
  const selection = (meta as DataTableMeta).selection;
  return selection;
}
